import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { LogOut, Plus, Edit, Archive, RotateCcw, Trash2, ArrowLeft, FileText, Download, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TemplateManagement = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [newTemplate, setNewTemplate] = useState({ name: '', prompt: '' });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
  }, [showArchived]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/document-templates?include_archived=${showArchived}`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης προτύπων');
    }
  };

  // Backup templates
  const handleBackupTemplates = async () => {
    try {
      const response = await axios.get(`${API}/document-templates/backup`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `prototypa_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Το backup κατέβηκε επιτυχώς');
    } catch (error) {
      toast.error('Σφάλμα δημιουργίας backup');
    }
  };

  // Import templates
  const handleImportTemplates = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const response = await axios.post(`${API}/document-templates/restore?mode=${importMode}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Επαναφορά: ${response.data.stats.added} προστέθηκαν, ${response.data.stats.updated} ενημερώθηκαν`);
      setImportDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα επαναφοράς');
    } finally {
      setLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/document-templates`, newTemplate);
      toast.success('Το πρότυπο δημιουργήθηκε');
      setNewTemplate({ name: '', prompt: '' });
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα δημιουργίας προτύπου');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!editingTemplate) return;

    setLoading(true);
    try {
      await axios.put(`${API}/admin/document-templates/${editingTemplate.template_id}`, {
        name: editingTemplate.name,
        prompt: editingTemplate.prompt
      });
      toast.success('Το πρότυπο ενημερώθηκε');
      setEditingTemplate(null);
      setEditDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης προτύπου');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/admin/document-templates/${templateId}`);
      toast.success('Το πρότυπο αρχειοθετήθηκε');
      fetchTemplates();
    } catch (error) {
      toast.error('Σφάλμα αρχειοθέτησης');
    }
  };

  const handleRestoreTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/admin/document-templates/${templateId}/restore`);
      toast.success('Το πρότυπο επαναφέρθηκε');
      fetchTemplates();
    } catch (error) {
      toast.error('Σφάλμα επαναφοράς');
    }
  };

  const handleHardDelete = async (templateId) => {
    try {
      await axios.delete(`${API}/admin/document-templates/${templateId}?hard_delete=true`);
      toast.success('Το πρότυπο διαγράφηκε οριστικά');
      fetchTemplates();
    } catch (error) {
      toast.error('Σφάλμα διαγραφής');
    }
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <div className="sidebar">
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Admin Panel</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <div className="space-y-2">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              className="w-full justify-start"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Πίσω στους Χρήστες
            </Button>
            <div className="px-4 py-2 bg-slate-100 rounded-md text-slate-900 font-medium">
              <FileText className="inline mr-2 h-4 w-4" />
              Πρότυπα Εγγράφων
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      <div className="main-content">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                Διαχείριση Προτύπων Εγγράφων
              </h1>
              <p className="text-slate-600 mt-1">
                Προσθήκη, επεξεργασία και διαγραφή προτύπων δικαιολογητικών με AI prompts
              </p>
            </div>

            <div className="flex gap-2">
              {/* Backup Button */}
              <Button
                variant="outline"
                onClick={handleBackupTemplates}
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
              >
                <Download className="mr-2 h-4 w-4" />
                Backup
              </Button>

              {/* Import Button */}
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50">
                    <Upload className="mr-2 h-4 w-4" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import Προτύπων</DialogTitle>
                    <DialogDescription>
                      Επαναφέρετε πρότυπα από αρχείο backup (.json)
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Τρόπος Επαναφοράς</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="importMode"
                            value="merge"
                            checked={importMode === 'merge'}
                            onChange={(e) => setImportMode(e.target.value)}
                            className="text-blue-600"
                          />
                          <span className="text-sm">Συγχώνευση (ενημέρωση υπαρχόντων)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="importMode"
                            value="replace"
                            checked={importMode === 'replace'}
                            onChange={(e) => setImportMode(e.target.value)}
                            className="text-blue-600"
                          />
                          <span className="text-sm text-rose-600">Αντικατάσταση (διαγραφή όλων)</span>
                        </label>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Αρχείο Backup</Label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleImportTemplates}
                        className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                    {loading && <p className="text-sm text-blue-600">Γίνεται επαναφορά...</p>}
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                onClick={() => setShowArchived(!showArchived)}
                className={showArchived ? 'bg-amber-50 border-amber-300' : ''}
              >
                <Archive className="mr-2 h-4 w-4" />
                {showArchived ? 'Απόκρυψη Αρχειοθετημένων' : 'Εμφάνιση Αρχειοθετημένων'}
              </Button>

              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 hover:bg-slate-800">
                    <Plus className="mr-2 h-4 w-4" />
                    Νέο Πρότυπο
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Νέο Πρότυπο Εγγράφου</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTemplate} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="template-name">Όνομα Δικαιολογητικού</Label>
                      <Input
                        id="template-name"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        placeholder="π.χ. Φορολογική Ενημερότητα"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="template-prompt">AI Prompt (προαιρετικό)</Label>
                      <Textarea
                        id="template-prompt"
                        value={newTemplate.prompt}
                        onChange={(e) => setNewTemplate({ ...newTemplate, prompt: e.target.value })}
                        placeholder="Εισάγετε το prompt για τον AI έλεγχο. Αν αφήσετε κενό, δεν θα γίνεται AI έλεγχος για αυτό το έγγραφο."
                        rows={10}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-slate-500">
                        Αν δεν συμπληρώσετε prompt, το δικαιολογητικό θα εμφανίζεται στη λίστα αλλά δεν θα ελέγχεται από AI.
                      </p>
                    </div>

                    <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading}>
                      {loading ? 'Δημιουργία...' : 'Δημιουργία'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Όνομα
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      AI Prompt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Κατάσταση
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Ενέργειες
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {templates.map((template) => (
                    <tr 
                      key={template.template_id} 
                      className={`hover:bg-slate-50 ${template.is_archived ? 'bg-amber-50/50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900">{template.name}</div>
                        <div className="text-xs text-slate-500">
                          {new Date(template.created_at).toLocaleDateString('el-GR')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {template.prompt ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                            ✓ Ενεργό ({template.prompt.length} χαρακτήρες)
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-slate-100 text-slate-600 text-xs font-medium">
                            — Χωρίς prompt
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {template.is_archived ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-amber-100 text-amber-700 text-xs font-medium">
                            Αρχειοθετημένο
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium">
                            Ενεργό
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingTemplate(template);
                              setEditDialogOpen(true);
                            }}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>

                          {template.is_archived ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestoreTemplate(template.template_id)}
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Οριστική Διαγραφή</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Είστε σίγουροι ότι θέλετε να διαγράψετε οριστικά το πρότυπο "{template.name}"; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleHardDelete(template.template_id)}
                                      className="bg-rose-600 hover:bg-rose-700"
                                    >
                                      Οριστική Διαγραφή
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          ) : (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Αρχειοθέτηση Προτύπου</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Είστε σίγουροι ότι θέλετε να αρχειοθετήσετε το πρότυπο "{template.name}"; Θα μπορείτε να το επαναφέρετε αργότερα.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleArchiveTemplate(template.template_id)}
                                    className="bg-amber-600 hover:bg-amber-700"
                                  >
                                    Αρχειοθέτηση
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {templates.length === 0 && (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-slate-500">
                        Δεν υπάρχουν πρότυπα. Δημιουργήστε το πρώτο σας πρότυπο.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Edit Template Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Επεξεργασία Προτύπου</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateTemplate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-template-name">Όνομα Δικαιολογητικού</Label>
                  <Input
                    id="edit-template-name"
                    value={editingTemplate?.name || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-template-prompt">AI Prompt</Label>
                  <Textarea
                    id="edit-template-prompt"
                    value={editingTemplate?.prompt || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, prompt: e.target.value })}
                    placeholder="Εισάγετε το prompt για τον AI έλεγχο..."
                    rows={15}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setEditDialogOpen(false);
                      setEditingTemplate(null);
                    }}
                    className="flex-1"
                  >
                    Ακύρωση
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-slate-900 hover:bg-slate-800"
                    disabled={loading}
                  >
                    {loading ? 'Αποθήκευση...' : 'Αποθήκευση'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

export default TemplateManagement;
